import React from 'react';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import useValidationSchema from './_form';
import { IPractitionerForm, IPractitionerFormComponent } from './_types';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import FormSelect from 'components/Form/Select/Select';
import FormSwitch from 'components/Form/Switch/Switch';
import FormDatePicker from 'components/Form/Date/Date';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';
import Header from 'components/Header/Header';
import SharedContactSections from '../SharedContactSections';
import { SimpleCheckboxArray } from 'components/Form/SimpleCheckboxArray/SimpleCheckboxArray';

const PractitionerFormComponent: React.FC<IPractitionerFormComponent> = ({
  isCreating,
  defaultValues,
  onSubmit,
  linkBack,
  compactMode = false,
  title,
  organizationItems = [],
}) => {
  const { t } = useTranslation('Practitioners');

  const { FormSchema } = useValidationSchema();

  const methods = useForm<IPractitionerForm>({
    resolver: yupResolver(FormSchema),
    defaultValues,
  });
  const { control, handleSubmit } = methods;

  const { fields: nameFields } = useFieldArray({ control, name: 'name' });
  const {
    fields: identifierFields,
    append: appendIdentifier,
    remove: removeIdentifier,
  } = useFieldArray({ control, name: 'identifier' });
  const {
    fields: telecomFields,
    append: appendTelecom,
    remove: removeTelecom,
  } = useFieldArray({ control, name: 'telecom' });
  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: 'address' });

  const genderOptions = [
    { id: 'male', label: t('form.gender.male') },
    { id: 'female', label: t('form.gender.female') },
    { id: 'other', label: t('form.gender.other') },
    { id: 'unknown', label: t('form.gender.unknown') },
  ];

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Header title={title} />
        <Papeer>
          <Grid container spacing={compactMode ? 1 : 2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">{t('PatientRegistry:basicInfo')}</Typography>
            </Grid>

            {nameFields.map((field, index) => (
              <Grid size={{ xs: 12 }} key={field.id}>
                <Grid container spacing={compactMode ? 1 : 2}>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <FormInput
                      control={control}
                      name={`name.${index}.prefix[0]`}
                      label={t('form.name.prefix')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <FormInput
                      control={control}
                      name={`name.${index}.family`}
                      label={t('form.name.family')}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <FormInput
                      control={control}
                      name={`name.${index}.given[0]`}
                      label={t('form.name.given')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <FormInput
                      control={control}
                      name={`name.${index}.suffix[0]`}
                      label={t('form.name.suffix')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}

            <Grid size={{ xs: 12 }}>
              <Grid container spacing={compactMode ? 1 : 2}>
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <FormSelect
                    control={control}
                    name="gender"
                    label={t('form.gender.title')}
                    items={genderOptions}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <FormDatePicker type="datePicker" name="birthDate" label={t('form.birthDate')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <Box sx={{ pt: 1 }}>
                    <FormSwitch
                      control={control}
                      name="active"
                      label={t('PatientRegistry:active')}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <SharedContactSections
              control={control}
              compactMode={compactMode}
              identifierInputMode="practitioner"
              identifierFields={identifierFields}
              appendIdentifier={appendIdentifier}
              removeIdentifier={removeIdentifier}
              telecomFields={telecomFields}
              appendTelecom={appendTelecom}
              removeTelecom={removeTelecom}
              addressFields={addressFields}
              appendAddress={appendAddress}
              removeAddress={removeAddress}
            />

            {organizationItems.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('form.organizations')}
                </Typography>
                <SimpleCheckboxArray
                  name="organizationIds"
                  items={organizationItems}
                  showCheckAllButton={false}
                  compactMode={compactMode}
                />
              </Grid>
            )}
          </Grid>
        </Papeer>

        <Papeer>
          <EntityButtons linkBack={linkBack} />
        </Papeer>
      </form>
    </FormProvider>
  );
};

export default PractitionerFormComponent;
